import {
  FlexBox,
  JoonUIColor,
  Loader,
  SPACING,
  TextButton,
  TrashIcon,
  Typography,
} from "@joonapp/web-shared"

import { useAddQuestTemplatesModalStore } from "./useAddQuestTemplatesModalStore"
import DifficultyBadge from "../../../components/badges/difficultyBadge/DifficultyBadge"
import { difficultyRewards, REPETITIONS } from "../../../constants"
import { useFamilyQuery } from "../../../networking/queries"
import { useDeleteQuestTemplateMutation } from "../../../networking/quests/mutations"
import { FamilyQuerySelect } from "../../../types"
import { QuestTemplate } from "../../../types/quests"
import {
  getRepetitionArrayFromRrule,
  getRepetitionIntervalFromRrule,
} from "../../../util/util"
import { useAddEditQuestStore } from "../addEditQuestModal/useAddEditQuestStore"

export const QuestTemplateItem = ({
  questTemplate,
}: {
  questTemplate: QuestTemplate
}) => {
  const {
    setQuestName,
    setQuestDifficulty,
    setDescription,
    setReqReview,
    setReqNotes,
    setReqPhoto,
    setRepetition,
    setRepeatsFrequency,
    setIsOngoing,
    setCategory,
    setTemplateId,
    setCustomTemplateId,
    setTimerLength,
    setReminderTimeMap,
    setMandatory,
    setRoutine,
  } = useAddEditQuestStore()
  const { onClose: onCloseAddTemplateModal } = useAddQuestTemplatesModalStore()
  const { onOpen: onOpenAddEditQuestModal } = useAddEditQuestStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const deleteQuestTemplateMutation = useDeleteQuestTemplateMutation()

  const setTemplate = () => {
    if (!children) return
    onOpenAddEditQuestModal()
    onCloseAddTemplateModal()
    setQuestName(questTemplate.title)
    setRoutine(questTemplate.routine)
    setQuestDifficulty(questTemplate.difficulty)
    setReqReview(questTemplate.requires_review)
    setCategory(questTemplate.skill)

    switch (questTemplate.type) {
      case "custom":
        setCustomTemplateId(questTemplate.id)
        setReqNotes(questTemplate.requires_completion_notes)
        setReqPhoto(questTemplate.requires_completion_photo)
        // note: reminderTime should never be sent up as an empty string. we change it to null before sending it up
        setReminderTimeMap({
          [questTemplate.routine]: questTemplate.reminder_time ?? "",
        })
        setTimerLength(questTemplate.timer_length)
        setMandatory(questTemplate.mandatory)

        if (questTemplate.description !== null) {
          setDescription(questTemplate.description!)
        }

        const repetitionArray = getRepetitionArrayFromRrule(
          questTemplate.recurrence
        )
        const hasRepetition = repetitionArray.some((day) => day)
        if (hasRepetition) {
          setRepetition(getRepetitionArrayFromRrule(questTemplate.recurrence))
          setRepeatsFrequency(
            getRepetitionIntervalFromRrule(questTemplate.recurrence)
          )
          setIsOngoing(false)
        } else {
          setRepetition(REPETITIONS.noDays)
          setIsOngoing(true)
        }
        break
      case "suggested":
        setTemplateId(questTemplate.id)
        if (questTemplate.repeats_daily) {
          setRepetition(REPETITIONS.daily)
          setIsOngoing(false)
        } else {
          setRepetition(REPETITIONS.noDays)
          setIsOngoing(true)
        }
        break
    }
  }

  const handleDelete = async () => {
    if (deleteQuestTemplateMutation.isLoading) return
    await deleteQuestTemplateMutation.mutateAsync(questTemplate.id)
  }

  return (
    <FlexBox
      direction="row"
      align="center"
      justify="space-between"
      gap={SPACING.space2}
      wrap={false}
      fullWidth
    >
      <FlexBox
        direction="row"
        onClick={setTemplate}
        align="center"
        wrap={false}
        gap={SPACING.space2}
        style={{
          padding: SPACING.space2,
          border: `1px solid ${JoonUIColor.border.default}`,
          borderRadius: SPACING.space2,
          cursor: "pointer",
          width: "100%",
        }}
      >
        <FlexBox direction="column">
          <Typography variant="bodySmallBold">{questTemplate.title}</Typography>
          {questTemplate.type === "suggested" && (
            <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
              {questTemplate.additional_text}
            </Typography>
          )}
        </FlexBox>
        <DifficultyBadge
          difficulty={questTemplate.difficulty}
          reward={difficultyRewards[questTemplate.difficulty]}
        />
      </FlexBox>
      {questTemplate.type === "custom" && (
        <TextButton
          label="Delete"
          onClick={handleDelete}
          color={JoonUIColor.semantic.destructive}
          style={{ padding: SPACING.space3 }}
        >
          {deleteQuestTemplateMutation.isLoading ? (
            <Loader
              size={16}
              thickness={2}
              color={JoonUIColor.semantic.primary}
            />
          ) : (
            <TrashIcon size={16} color={JoonUIColor.semantic.destructive} />
          )}
        </TextButton>
      )}
    </FlexBox>
  )
}
